/**
 * Driver ↔ vehicle matching engine — the single source of truth for
 * "may this soldier drive this vehicle?".
 *
 * Rules, in precedence order:
 *  1. An explicit block wins over everything. Either side of the pair is
 *     enough: the vehicle's `disallowedSoldierIds` or the soldier's
 *     `disallowedVehicleIds`. A command decision is never overridden by a
 *     complete licence.
 *  2. A soldier with no military driving licence, or an expired one, drives
 *     nothing.
 *  3. The licence weight class must reach the vehicle's class. The scale is
 *     cumulative — a `heavy` licence covers `medium` and `light`. This is the
 *     whole of the licensing question: operating endorsements
 *     (`Soldier.licensePermits`) are informational and never block a pairing.
 *  4. Both the soldier and the vehicle must be free, unless the caller is
 *     planning ahead and passes `ignoreAvailability`.
 *
 * Every failing rule is collected, so the UI can explain the full picture
 * rather than only the first thing that went wrong.
 */
import { vehicleCallSign } from './military-id';
import {
  MATCH_FAILURE_CODES,
  MATCH_FAILURE_LABELS,
  WEIGHT_CATEGORY_LABELS,
  WEIGHT_CATEGORY_RANK,
} from '../types/fleet';
import type {
  DriverVehicleMatch,
  MatchFailure,
  MatchFailureCode,
  Soldier,
  Vehicle,
} from '../types/fleet';

export interface MatchOptions {
  /** Evaluation timestamp, for licence expiry. Defaults to now. */
  now?: number;
  /** Skip the soldier/vehicle availability rules when planning ahead. */
  ignoreAvailability?: boolean;
}

/** Licence renewals this close to expiry pull the fitness score down. */
export const LICENSE_RENEWAL_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/** A licence class this far above the vehicle's costs a point of fitness. */
const OVERQUALIFIED_PENALTY = 10;

/**
 * Answers the assignment question for one soldier and one vehicle.
 * This is the only function allowed to decide it.
 */
export function checkDriverVehicleMatch(
  soldier: Soldier,
  vehicle: Vehicle,
  options: MatchOptions = {},
): DriverVehicleMatch {
  const now = options.now ?? Date.now();
  const collected = new Map<MatchFailureCode, string>();

  // 1. Explicit command-level block — either direction.
  const blockedByVehicle = vehicle.disallowedSoldierIds?.includes(soldier.id) ?? false;
  const blockedBySoldier = soldier.disallowedVehicleIds?.includes(vehicle.id) ?? false;
  if (blockedByVehicle || blockedBySoldier) {
    collected.set(
      'EXPLICIT_DISALLOW',
      `${MATCH_FAILURE_LABELS.EXPLICIT_DISALLOW} (${vehicleCallSign(vehicle)})`,
    );
  }

  // 2. Licence exists and is in date.
  const licenseRank = soldier.licenseWeightCategory
    ? WEIGHT_CATEGORY_RANK[soldier.licenseWeightCategory]
    : 0;
  if (licenseRank === 0) {
    collected.set('NO_LICENSE', MATCH_FAILURE_LABELS.NO_LICENSE);
  }

  const expiresAt = soldier.licenseExpiresAt ? Date.parse(soldier.licenseExpiresAt) : NaN;
  const licenseExpired = Number.isFinite(expiresAt) && expiresAt <= now;
  if (licenseExpired) {
    collected.set('LICENSE_EXPIRED', MATCH_FAILURE_LABELS.LICENSE_EXPIRED);
  }

  // 3. Weight class.
  const requiredRank = WEIGHT_CATEGORY_RANK[vehicle.weightCategory];
  const missingWeightCategory =
    licenseRank > 0 && licenseRank < requiredRank ? vehicle.weightCategory : null;
  if (missingWeightCategory) {
    collected.set(
      'MISSING_WEIGHT_CATEGORY',
      `${MATCH_FAILURE_LABELS.MISSING_WEIGHT_CATEGORY}: ${WEIGHT_CATEGORY_LABELS[missingWeightCategory]}`,
    );
  }

  // 4. Availability.
  if (!options.ignoreAvailability) {
    if (soldier.availability !== 'available') {
      collected.set('SOLDIER_UNAVAILABLE', MATCH_FAILURE_LABELS.SOLDIER_UNAVAILABLE);
    }
    if (vehicle.status !== 'available') {
      collected.set('VEHICLE_UNAVAILABLE', MATCH_FAILURE_LABELS.VEHICLE_UNAVAILABLE);
    }
  }

  const failures: MatchFailure[] = MATCH_FAILURE_CODES.filter((code) => collected.has(code)).map(
    (code) => ({ code, message: collected.get(code) as string }),
  );

  const ok = failures.length === 0;
  return {
    ok,
    soldierId: soldier.id,
    vehicleId: vehicle.id,
    reason: ok ? null : failures[0].code,
    failures,
    missingWeightCategory,
    score: ok ? fitnessScore(soldier, licenseRank, requiredRank, expiresAt, now) : 0,
  };
}

/**
 * How good an eligible pairing is, 0–100. Sending the only articulated-licence
 * driver out in a van is legal but wasteful, so headroom costs points; so does
 * a licence about to expire mid-mission.
 */
function fitnessScore(
  soldier: Soldier,
  licenseRank: number,
  requiredRank: number,
  expiresAt: number,
  now: number,
): number {
  let score = 100;
  score -= Math.max(0, licenseRank - requiredRank) * OVERQUALIFIED_PENALTY;
  if (Number.isFinite(expiresAt) && expiresAt - now < LICENSE_RENEWAL_WINDOW_MS) score -= 15;
  if (soldier.availability !== 'available') score -= 20;
  return Math.max(0, Math.min(100, score));
}

/** True when the pairing is allowed — the shorthand for guards and filters. */
export function canDriveVehicle(
  soldier: Soldier,
  vehicle: Vehicle,
  options?: MatchOptions,
): boolean {
  return checkDriverVehicleMatch(soldier, vehicle, options).ok;
}

/** Eligible drivers for a vehicle, best fit first, then by name. */
export function findEligibleDrivers(
  soldiers: readonly Soldier[],
  vehicle: Vehicle,
  options?: MatchOptions,
): DriverVehicleMatch[] {
  return soldiers
    .map((soldier) => checkDriverVehicleMatch(soldier, vehicle, options))
    .filter((match) => match.ok)
    .sort((a, b) => b.score - a.score || a.soldierId.localeCompare(b.soldierId));
}

/** Vehicles a soldier may take out, best fit first. */
export function findEligibleVehicles(
  soldier: Soldier,
  vehicles: readonly Vehicle[],
  options?: MatchOptions,
): DriverVehicleMatch[] {
  return vehicles
    .map((vehicle) => checkDriverVehicleMatch(soldier, vehicle, options))
    .filter((match) => match.ok)
    .sort((a, b) => b.score - a.score || a.vehicleId.localeCompare(b.vehicleId));
}

/** One-line Hebrew summary of a match result, for lists and tooltips. */
export function describeMatch(match: DriverVehicleMatch): string {
  if (match.ok) return `מתאים לנהיגה (ציון ${match.score})`;
  return match.failures.map((failure) => failure.message).join(' · ');
}
